import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Camera, Shield, Eye, Monitor, Cloud, Smartphone, CheckCircle2, ArrowRight, Building2, Hospital, School, Home } from 'lucide-react';

const CCTVSurveillance = () => {
  const solutions = [
    {
      icon: Camera,
      title: "HD/4K CCTV Cameras",
      description: "High-definition surveillance cameras with night vision, motion detection, and weatherproof features."
    },
    {
      icon: Monitor,
      title: "Video Management Systems",
      description: "Centralized control and monitoring with recording, playback, and intelligent video analytics."
    },
    {
      icon: Cloud,
      title: "Cloud Storage Solutions",
      description: "Secure cloud-based storage with remote access and automated backup systems."
    },
    {
      icon: Eye,
      title: "AI-Powered Analytics",
      description: "Advanced video analytics including face recognition, object detection, and behavior analysis."
    },
    {
      icon: Smartphone,
      title: "Mobile Monitoring",
      description: "24/7 remote monitoring via mobile apps with real-time alerts and notifications."
    },
    {
      icon: Shield,
      title: "Perimeter Security",
      description: "Complete perimeter protection with thermal cameras and intrusion detection systems."
    }
  ];

  const sectors = [
    { icon: Building2, name: "Government & Defence", desc: "High-security surveillance for critical infrastructure" },
    { icon: Hospital, name: "Healthcare", desc: "Patient safety and facility monitoring" },
    { icon: School, name: "Educational Institutes", desc: "Campus safety and access control" },
    { icon: Home, name: "Residential", desc: "Home security and smart monitoring" }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>CCTV Installation & AMC | CNGTPL Surveillance Solutions</title>
        <meta name="description" content="Comprehensive CCTV surveillance solutions with professional installation and AMC for defence, government, hotels, hospitals, and infrastructure." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="bg-blue-100 text-blue-700 mb-6">Security & Surveillance</Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            CCTV Installation & <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">AMC Services</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Comprehensive surveillance solutions with advanced security cameras, professional installation, and reliable AMC services for diverse industries.
          </p>
          <Link to="/contact">
            <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-6 text-lg">
              Get Free Security Assessment
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* About */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-lg text-gray-700 leading-relaxed">
            We offer a comprehensive range of advanced security and surveillance solutions to meet the ever-changing requirements of different industries. 
            At present, we are serving many different verticals comprising <strong>defence, government, hotels, hospitals, educational institutes, homes, infrastructure, and transportation</strong> among others. 
            Being a major surveillance system provider, we bring <strong>efficient, reliable, scalable, and integrated solutions</strong> to our customers.
          </p>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Surveillance Solutions</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Complete CCTV systems with cutting-edge technology</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <Card key={index} className="p-8 hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200 bg-white">
                <solution.icon className="w-12 h-12 text-blue-600 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">{solution.title}</h3>
                <p className="text-gray-600">{solution.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Industries We Serve</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {sectors.map((sector, index) => (
              <Card key={index} className="p-6 text-center hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200">
                <sector.icon className="w-10 h-10 text-blue-600 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-gray-900 mb-2">{sector.name}</h3>
                <p className="text-sm text-gray-600">{sector.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* AMC Services */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">AMC Services Included</h2>
            <p className="text-lg text-gray-600">Comprehensive maintenance contracts for uninterrupted security</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Regular preventive maintenance",
              "24/7 technical support",
              "Camera cleaning and adjustment",
              "Software updates",
              "Hardware replacement",
              "System health monitoring",
              "Emergency repairs",
              "Annual system audits",
              "Performance optimization"
            ].map((service, index) => (
              <div key={index} className="flex items-center space-x-3 bg-white p-6 rounded-lg shadow-md">
                <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-500 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Secure Your Premises Today</h2>
          <p className="text-xl mb-8 opacity-90">Get a free consultation and custom security assessment from our experts</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Request Quote
              </Button>
            </Link>
            <a href="tel:+919315649601">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-6 text-lg">
                Call: +91 9315649601
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CCTVSurveillance;